import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { API } from "@/App";
import axios from "axios";
import { 
  Filter, 
  Search,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  X,
  RefreshCw,
  ArrowRight,
  Building2
} from "lucide-react";

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "Large Cap", label: "Large Cap" },
  { value: "Mid Cap", label: "Mid Cap" },
  { value: "Small Cap", label: "Small Cap" },
  { value: "ELSS", label: "ELSS Tax Saver" },
  { value: "Hybrid", label: "Hybrid" },
  { value: "Debt", label: "Debt" },
  { value: "Index", label: "Index Funds" }
];

const SORT_OPTIONS = [
  { value: "returns1Y", label: "1 Year Returns" },
  { value: "returns3Y", label: "3 Year Returns" },
  { value: "returns5Y", label: "5 Year Returns" },
  { value: "current_nav", label: "NAV" }
];

export const FundScreenerPage = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalFound, setTotalFound] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    category: "all",
    minReturn1Y: 0,
    maxReturn1Y: 100,
    minReturn3Y: 0,
    fundHouse: "",
    sortBy: "returns1Y",
    sortOrder: "desc"
  });

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== "all") params.append("category", filters.category);
      if (filters.minReturn1Y > 0) params.append("min_return_1y", filters.minReturn1Y);
      if (filters.maxReturn1Y < 100) params.append("max_return_1y", filters.maxReturn1Y);
      if (filters.minReturn3Y > 0) params.append("min_return_3y", filters.minReturn3Y);
      if (filters.fundHouse) params.append("fund_house", filters.fundHouse);
      params.append("sort_by", filters.sortBy);
      params.append("sort_order", filters.sortOrder);
      params.append("limit", "50");

      const response = await axios.get(`${API}/funds/screener?${params}`);
      setFunds(response.data.funds || []);
      setTotalFound(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching funds:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunds();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchFunds();
  };

  const resetFilters = () => {
    setFilters({
      category: "all",
      minReturn1Y: 0,
      maxReturn1Y: 100,
      minReturn3Y: 0,
      fundHouse: "",
      sortBy: "returns1Y",
      sortOrder: "desc"
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fund-screener-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Filter className="w-8 h-8 text-primary" />
            Fund Screener
          </h1>
          <p className="text-muted-foreground mt-1">
            Filter and find mutual funds that match your investment criteria
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Panel */}
          <Card className="bg-card/50 border-border/50 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                Filters
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <X className="w-4 h-4 mr-1" /> Reset
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={filters.category} 
                  onValueChange={(v) => handleFilterChange("category", v)}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 1Y Returns Range */}
              <div className="space-y-2">
                <Label>1 Year Returns (%)</Label>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <span>{filters.minReturn1Y}%</span>
                  <span className="flex-1 text-center">to</span>
                  <span>{filters.maxReturn1Y}%</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-8">Min:</span>
                    <Slider
                      value={[filters.minReturn1Y]}
                      onValueChange={([v]) => handleFilterChange("minReturn1Y", v)}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-8">Max:</span>
                    <Slider
                      value={[filters.maxReturn1Y]}
                      onValueChange={([v]) => handleFilterChange("maxReturn1Y", v)}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* 3Y Returns Min */}
              <div className="space-y-2">
                <Label>Min 3Y Returns (%)</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[filters.minReturn3Y]}
                    onValueChange={([v]) => handleFilterChange("minReturn3Y", v)}
                    max={50}
                    step={5}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-10">{filters.minReturn3Y}%</span>
                </div>
              </div>

              {/* Fund House */}
              <div className="space-y-2">
                <Label>Fund House</Label>
                <Input
                  placeholder="e.g., HDFC, ICICI"
                  value={filters.fundHouse}
                  onChange={(e) => handleFilterChange("fundHouse", e.target.value)}
                  data-testid="fund-house-input"
                />
              </div>

              {/* Sort By */}
              <div className="space-y-2">
                <Label>Sort By</Label>
                <Select 
                  value={filters.sortBy} 
                  onValueChange={(v) => handleFilterChange("sortBy", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Order */}
              <div className="flex gap-2">
                <Button
                  variant={filters.sortOrder === "desc" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => handleFilterChange("sortOrder", "desc")}
                >
                  <TrendingDown className="w-4 h-4 mr-1" /> High to Low
                </Button>
                <Button
                  variant={filters.sortOrder === "asc" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => handleFilterChange("sortOrder", "asc")}
                >
                  <TrendingUp className="w-4 h-4 mr-1" /> Low to High
                </Button>
              </div>

              {/* Search Button */}
              <Button 
                className="w-full" 
                onClick={handleSearch}
                disabled={loading}
                data-testid="search-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search Funds
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="lg:col-span-3 space-y-4">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">
                Found <span className="font-semibold text-foreground">{totalFound}</span> funds matching your criteria
              </p>
            </div>

            {/* Results Table */}
            {loading ? (
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={`skeleton-${i}`} className="animate-pulse flex items-center gap-4">
                        <div className="w-10 h-10 bg-muted rounded" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-3/4" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          {[...Array(3)].map((_, j) => (
                            <div key={`skeleton-col-${j}`} className="h-8 w-16 bg-muted rounded" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : funds.length > 0 ? (
              <Card className="bg-card/50 border-border/50 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium text-muted-foreground">#</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Fund Name</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Category</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">NAV</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">1Y Return</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">3Y Return</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">5Y Return</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funds.map((fund, index) => (
                        <tr 
                          key={fund.schemeCode} 
                          className="border-t border-border/50 hover:bg-muted/30 transition-colors"
                          data-testid={`fund-row-${fund.schemeCode}`}
                        >
                          <td className="p-4 text-muted-foreground">{index + 1}</td>
                          <td className="p-4">
                            <Link 
                              to={`/research/${fund.schemeCode}`}
                              className="font-medium text-foreground hover:text-primary transition-colors"
                            >
                              {fund.schemeName}
                            </Link>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Building2 className="w-3 h-3" />
                              {fund.fundHouse || 'N/A'}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                              {fund.category}
                            </span>
                          </td>
                          <td className="p-4 text-right font-medium">
                            ₹{fund.current_nav?.toFixed(2) || '-'}
                          </td>
                          <td className={`p-4 text-right font-bold ${(fund.returns1Y || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {fund.returns1Y ? `${fund.returns1Y >= 0 ? '+' : ''}${fund.returns1Y.toFixed(2)}%` : '-'}
                          </td>
                          <td className={`p-4 text-right font-bold ${(fund.returns3Y || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {fund.returns3Y ? `${fund.returns3Y >= 0 ? '+' : ''}${fund.returns3Y.toFixed(2)}%` : '-'}
                          </td>
                          <td className={`p-4 text-right font-bold ${(fund.returns5Y || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {fund.returns5Y ? `${fund.returns5Y >= 0 ? '+' : ''}${fund.returns5Y.toFixed(2)}%` : '-'}
                          </td>
                          <td className="p-4 text-center">
                            <Link to={`/research/${fund.schemeCode}`}>
                              <Button variant="ghost" size="sm">
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-8 text-center">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No funds found</h3>
                  <p className="text-muted-foreground mt-2">
                    Try adjusting your filters to find matching funds.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
